import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";
import {
  Field,
  GhostButton,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  StatusBadge,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = { code: "", name: "", status: "active" };

const LocationsPage = () => {
  const { selectedLocationId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, selectedLocationId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => rows, [rows]);

  const startNew = () => {
    setEditing(null);
    setForm(empty);
    setErr(null);
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({ code: row.code, name: row.name, status: row.status });
    setErr(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.code.trim() || !form.name.trim()) {
      setErr("Code and Name are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/locations/${editing.id}`, form);
        toast.success("Location updated");
      } else {
        await api.post("/locations", form);
        toast.success("Location created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/locations/${row.id}/status`, { status: next });
      toast.success(next === "active" ? "Activated" : "Inactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="locations-page">
      <AppHeader />
      <PageShell
        eyebrow="Masters · Locations"
        title="Locations"
        subtitle="Sites & branches. Everything below rolls up into a Location."
        primaryAction={
          <PrimaryButton testId="loc-add" onClick={startNew}>
            <Plus className="mr-1 inline h-4 w-4" /> Add Location
          </PrimaryButton>
        }
        filters={<SearchBox value={q} onChange={setQ} placeholder="Search code or name…" testId="loc-search" />}
        testId="locations-shell"
      >
        <div
          className="overflow-hidden rounded-2xl border bg-white shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div className="hidden grid-cols-[1fr_2fr_120px_140px] gap-4 bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid">
            <div>Code</div>
            <div>Name</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && (
            <div className="px-5 py-10 text-center text-sm text-vision-slate/60" data-testid="loc-loading">
              Loading…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="px-5 py-14 text-center text-sm text-vision-slate/60" data-testid="loc-empty">
              No locations yet. Click <span className="font-semibold text-vision-navy">Add Location</span> to create one.
            </div>
          )}

          {!loading &&
            filtered.map((row) => (
              <div
                key={row.id}
                data-testid={`loc-row-${row.id}`}
                className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1fr_2fr_120px_140px] md:gap-4"
                style={{ borderColor: "rgba(52,73,94,0.08)" }}
              >
                <div className="font-display text-sm font-bold text-vision-ink">{row.code}</div>
                <div className="text-sm text-vision-slate">{row.name}</div>
                <div><StatusBadge active={row.status === "active"} /></div>
                <div className="flex items-center justify-end gap-2">
                  <GhostButton testId={`loc-edit-${row.id}`} onClick={() => startEdit(row)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </GhostButton>
                  <GhostButton testId={`loc-toggle-${row.id}`} onClick={() => toggleStatus(row)}>
                    {row.status === "active"
                      ? (<><PowerOff className="h-3.5 w-3.5" /> Inactivate</>)
                      : (<><Power className="h-3.5 w-3.5" /> Activate</>)}
                  </GhostButton>
                </div>
              </div>
            ))}
        </div>
      </PageShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="loc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-vision-ink">
              {editing ? "Edit Location" : "Add Location"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="mt-2 grid gap-4">
            <Field label="Code">
              <input
                className={inputCls}
                data-testid="loc-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. BLR"
                maxLength={32}
              />
            </Field>
            <Field label="Name">
              <input
                className={inputCls}
                data-testid="loc-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Bengaluru"
                maxLength={120}
              />
            </Field>
            <Field label="Status">
              <select
                className={selectCls}
                data-testid="loc-form-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            {err && (
              <div
                data-testid="loc-form-error"
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(231, 76, 60, 0.08)",
                  borderColor: "rgba(231, 76, 60, 0.3)",
                  color: "var(--vision-error)",
                }}
              >
                {err}
              </div>
            )}

            <DialogFooter className="mt-2 gap-2 sm:justify-end">
              <OutlineButton type="button" testId="loc-form-cancel" onClick={() => setOpen(false)}>
                Cancel
              </OutlineButton>
              <PrimaryButton type="submit" testId="loc-form-submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
